/**
 * AgentDisputePanel — agent-side surface to file / withdraw dispute.
 * Phase 2D P2 (2026-04-24)
 *
 * POST /field-activities/{id}/dispute { type: "review"|"ownership", note ≥20 }
 * POST /field-activities/{id}/dispute/withdraw
 *
 * Visible when review.state in {approved, rejected} OR ownership_conflict true.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, ShieldAlert, Undo2 } from "lucide-react";

export default function AgentDisputePanel({ activity, onReload }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("review");
  const [note, setNote] = useState("");

  if (!activity) return null;
  const state = activity?.review?.state;
  const ownershipConflict = !!activity?.ownership_conflict;
  const dispute = activity?.dispute;
  const eligible = ["approved", "rejected"].includes(state) || ownershipConflict;
  if (!eligible && !dispute) return null;

  const file = async () => {
    if (note.trim().length < 20) return toast.error("Note must be ≥20 characters");
    setBusy(true);
    try {
      await api.post(`/field-activities/${activity.id}/dispute`, { type, note });
      toast.success("Dispute filed");
      setOpen(false); setNote("");
      onReload && (await onReload());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Dispute failed");
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw your dispute?")) return;
    setBusy(true);
    try {
      await api.post(`/field-activities/${activity.id}/dispute/withdraw`);
      toast.success("Dispute withdrawn");
      onReload && (await onReload());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Withdraw failed");
    } finally { setBusy(false); }
  };

  const isOpen = dispute?.state === "open";

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3" data-testid="agent-dispute-panel">
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert className="w-4 h-4 text-amber-700" />
        <div className="text-sm font-semibold text-amber-900">Dispute</div>
        {dispute && <Badge className="bg-fuchsia-600 text-white" data-testid="agent-dispute-state">{dispute.state}</Badge>}
      </div>

      {dispute && (
        <div className="text-xs text-amber-900 mb-2" data-testid="agent-dispute-detail">
          Type: {dispute.type} · Opened: {dispute.opened_at}
          {dispute.note && <div className="italic mt-1">"{dispute.note}"</div>}
          {dispute.resolution && <div className="mt-1">Resolution: <strong>{dispute.resolution}</strong></div>}
          {dispute.resolution_note && <div className="italic">"{dispute.resolution_note}"</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {eligible && !isOpen && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="agent-dispute-file-open">
            File dispute
          </Button>
        )}
        {isOpen && (
          <Button size="sm" variant="outline" onClick={withdraw} disabled={busy} data-testid="agent-dispute-withdraw">
            <Undo2 className="w-4 h-4 mr-1" /> Withdraw
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 border-t border-amber-200 pt-3 space-y-2">
          <label className="text-xs text-amber-900">Type</label>
          <select className="w-full border rounded p-2 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="agent-dispute-type">
            <option value="review">Review outcome</option>
            <option value="ownership">Ownership</option>
          </select>
          <label className="text-xs text-amber-900">Reason (≥20 chars) — {note.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm bg-white" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="agent-dispute-note" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={file} disabled={busy} data-testid="agent-dispute-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "File dispute"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
